/**
 * Has the model stopped describing the estate it was fitted on?
 *
 * `survival` gives the predictions intervals; this asks whether anybody should
 * still be acting on them — and it is mostly an exercise in NOT ANSWERING.
 *
 * THREE QUESTIONS, KEPT APART, because the commonest drift dashboard collapses
 * them into one traffic light and the light is usually the wrong colour:
 *
 *   inputs moved      cheap, immediate, and NOT evidence the model is wrong.
 *                     A fleet that aged a year has moved its burn-hours
 *                     distribution and the model may be perfectly fine.
 *   still calibrated  does a predicted 20% still happen about 20% of the time.
 *                     Needs outcomes, so it is slow, and it goes first.
 *   performance fell  the only one that says the model is WRONG.
 *
 * THE BLIND SPOT, and the reason `conclusive` exists: a prediction with a
 * ninety-day horizon made last week cannot have been scored. It is not right,
 * not wrong — nobody has found out yet. So the most recent period, the one
 * everybody looks at and the one they want the check to cover, is exactly the
 * period that can say nothing. A check whose predictions are mostly still
 * pending is not a green light; it is an empty one.
 */

export interface DriftCheck {
  id: number;
  modelKey: string;
  windowFrom: string;
  windowTo: string;
  predictions: number;
  decided: number;
  undecided: number;
  /** Predictions whose horizon has not elapsed. Not wrong — not yet knowable. */
  pending: number;
  /** What fraction of the window could be judged at all. */
  judgeableFraction?: number;
  /** Where performance changed, when a change point was found. */
  changePoint?: string;
  /** The platform's own sentence. It is the finding, not a summary of one. */
  verdict: string;
  /**
   * Whether this check can support a conclusion. False means the window is
   * too pending or too thin — which must never be drawn as "no drift".
   */
  conclusive: boolean;
  checkedAt?: string;
}

export interface DriftDue {
  modelKey: string;
  referenceId?: string;
  horizonDays?: number;
  windowDays?: number;
  everyDays?: number;
  lastChecked?: string;
}

export interface DriftSchedule {
  due: DriftDue[];
  waiting: DriftDue[];
}

export type DriftState = "drifted" | "holding" | "inconclusive";

/**
 * What a check amounts to.
 *
 * `conclusive` is read FIRST. A check that could not judge its window has no
 * verdict to give, and reading the prose for the word "drifted" before
 * checking that would turn an empty window into a clean bill of health — the
 * exact failure the module's own header warns about.
 */
export function state(c: DriftCheck): DriftState {
  if (!c.conclusive) return "inconclusive";
  /* the platform's pass reads "No drift detected…"; only "has drifted" is a drift (insight/drift.py) */
  return /has drifted/i.test(c.verdict) ? "drifted" : "holding";
}

export const STATE_LABEL: Record<DriftState, string> = {
  drifted: "Drifted",
  holding: "Still describes the estate",
  inconclusive: "Cannot say yet",
};

/**
 * How much of the window was actually judgeable, as a percentage.
 *
 * Null when the check reports no fraction — not 100. A missing coverage figure
 * is the one case where assuming the best is most dangerous.
 */
export function judgeablePct(c: DriftCheck): number | null {
  if (c.judgeableFraction == null) return null;
  return Math.round(c.judgeableFraction * 100);
}

/** Drifted first, then inconclusive, then holding: worst news at the top. */
export function byUrgency(checks: DriftCheck[]): DriftCheck[] {
  const rank: Record<DriftState, number> = { drifted: 0, inconclusive: 1, holding: 2 };
  return [...checks].sort((a, b) => rank[state(a)] - rank[state(b)]);
}
